import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MaskitoDirective } from '@maskito/angular';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideLandmark, lucideMail, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { BankAccount, CompanyService } from '../../services/company.service';
import { IBAN_MASK, ibanError, normalizeIban } from '../../utils/iban.utils';

/**
 * IBAN form for a member's payout account (the network transfers settlements to
 * it) or a network's debit account (the Sammelüberweisung is drawn from it). Used
 * in settings and as the last onboarding step of a member.
 */
@Component({
  selector: 'app-bank-account-form',
  standalone: true,
  imports: [
    FormsModule,
    MaskitoDirective,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmFieldImports,
    HlmInputImports,
    HlmSpinnerImports,
    HlmTypographyImports,
  ],
  providers: [provideIcons({ lucideLandmark, lucideMail, lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  templateUrl: './bank-account-form.component.html',
})
export class BankAccountFormComponent {
  private readonly companyService = inject(CompanyService);

  /** Whose account this is - changes the wording, not the behaviour. */
  readonly audience = input<'member' | 'network'>('member');
  readonly submitLabel = input('Speichern');
  readonly saved = output<BankAccount>();

  readonly company = this.companyService.company;
  readonly editing = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal(false);
  readonly touched = signal(false);

  readonly iban = signal('');
  readonly ibanMask = IBAN_MASK;
  readonly holder = signal('');
  readonly bic = signal('');

  readonly ibanProblem = computed(() => (this.touched() ? ibanError(this.iban()) : null));
  readonly showForm = computed(() => this.editing() || !this.company()?.has_bank_account);
  /** A member's new IBAN waiting for the link we e-mailed; the old one still pays out. */
  readonly pendingIban = computed(() => this.company()?.pending_bank_iban_masked ?? null);
  readonly pendingUntil = computed(() => {
    const until = this.company()?.pending_bank_expires_at;
    return until
      ? new Date(until.endsWith('Z') ? until : `${until}Z`).toLocaleString('de-DE', {
          dateStyle: 'short',
          timeStyle: 'short',
        })
      : '';
  });
  readonly cancelling = signal(false);

  cancelPending(): void {
    this.cancelling.set(true);
    this.companyService.cancelBankAccountChange().subscribe({
      next: () => this.cancelling.set(false),
      error: () => {
        this.cancelling.set(false);
        this.error.set('Die Änderung konnte nicht zurückgezogen werden.');
      },
    });
  }

  startEditing(): void {
    this.iban.set('');
    this.holder.set(this.company()?.bank_account_holder ?? this.company()?.name ?? '');
    this.bic.set(this.company()?.bank_bic ?? '');
    this.error.set(null);
    this.success.set(false);
    this.touched.set(false);
    this.editing.set(true);
  }

  submit(): void {
    this.touched.set(true);
    if (ibanError(this.iban()) || this.holder().trim().length < 2) return;
    this.saving.set(true);
    this.error.set(null);
    this.companyService
      .updateBankAccount({
        iban: normalizeIban(this.iban()),
        account_holder: this.holder(),
        bic: this.bic() || null,
      })
      .subscribe({
        next: (account) => {
          this.saving.set(false);
          this.editing.set(false);
          this.success.set(true);
          this.saved.emit(account);
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set(
            err.error?.detail || 'Die Bankverbindung konnte nicht gespeichert werden.',
          );
        },
      });
  }
}
